import { UIEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuthentication, useFlightRooms, useSearchPaginatedFlightRooms } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomSummary } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { hasOpenTasks } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { ConfirmationModal, ModalOpener, Spinner, toastError } from '@connect-x-app/web-ui';
import { hasReachedScrollEnd } from '@connect-x-app/web-utils';
import classNames from 'classnames';

import { FlightroomItem } from './flightroomitem/FlightroomItem';
import { RequestFlightroomAccessModal } from './requestFlightroomAccessModal/RequestFlightroomAccessModal';

type Props = {
  resetSearchQuery: () => void;
  searchQuery: string;
};

export const SearchedFlightRoomList = ({ resetSearchQuery, searchQuery }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { joinFlightRoom, selectedFlightRoom } = useFlightRooms();
  const [isJoining, setIsJoining] = useState<boolean>(false);

  const {
    canJoinFlightRooms,
    canRequestAccess,
    canSearchFlightRooms,
    canViewFlightRooms,
    hasPendingAccessRequest,
    isActiveMemberOfRoom,
  } = useAuthentication();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingSearch,
  } = useSearchPaginatedFlightRooms(searchQuery);

  useEffect(() => {
    if (!canSearchFlightRooms || !canViewFlightRooms) {
      resetSearchQuery();
    }
  }, [canSearchFlightRooms, canViewFlightRooms, resetSearchQuery]);

  const handleJoinFlightroom = async (room: FlightRoomSummary) => {
    setIsJoining(true);
    const result = await joinFlightRoom(room.id);
    setIsJoining(false);
    if (result?.ok) {
      resetSearchQuery();
      navigate(`/dashboard/flightrooms/${room.id}`);
    } else {
      toastError(t('TOAST_ERROR'));
    }
  };

  const onClickSearchableFlightroom = (room: FlightRoomSummary) => {
    if (room.id === selectedFlightRoom?.id) {
      resetSearchQuery();
      return;
    }

    if (isActiveMemberOfRoom(room)) {
      resetSearchQuery();
      navigate(`/dashboard/flightrooms/${room.id}`);
      return;
    }

    if (canJoinFlightRooms) {
      if (hasOpenTasks(room.tasks)) {
        ModalOpener.instance.open({
          render: () => (
            <ConfirmationModal
              confirmText={t('TASKS.JOIN_MODAL.CONFIRM')}
              content={t('TASKS.JOIN_MODAL.CONTENT')}
              onConfirm={() => {
                handleJoinFlightroom(room);
              }}
              title={t('TASKS.JOIN_MODAL.TITLE')}
            />
          ),
        });
      } else {
        handleJoinFlightroom(room);
      }
      return;
    }

    if (hasPendingAccessRequest(room)) {
      return;
    }

    if (canRequestAccess) {
      ModalOpener.instance.open({
        render: () => (
          <RequestFlightroomAccessModal
            chatroomId={room.id}
            content={t('FLIGHTROOMS.REQUEST_ACCESS.MESSAGE', { flightroom: room.flightNumber })}
            onError={() => toastError(t('TOAST_ERROR'))}
            onSuccess={resetSearchQuery}
            room={room}
            title={t('FLIGHTROOMS.REQUEST_ACCESS.TITLE')}
          />
        ),
      });
    }
  };

  if (isLoadingSearch) {
    return (
      <section className={classNames('flightrooms-content-container', 'is-loading')}>
        <Spinner size="large" />
      </section>
    );
  }

  if (data?.pages?.length === 0) {
    return (
      <section className={classNames('flightrooms-content-container', 'is-empty-search')}>
        <Text className="flightrooms-empty-search-text" color={COLORS.zulu.$10} size="MD" weight="bold">
          {`${t('FLIGHTROOMS.EMPTY_SEARCH.TITLE')}, ${t('FLIGHTROOMS.EMPTY_SEARCH.DESCRIPTION')}`}
        </Text>
      </section>
    );
  }

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    if (hasReachedScrollEnd(e) && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <section
      className={classNames('flightrooms-content-container')}
      data-test={FLIGHTROOM_TEST_IDS.search.results}
      onScroll={handleScroll}
    >
      {data?.pages.map(page =>
        page.items?.map(room => (
          <FlightroomItem
            disabled={isJoining}
            displayAccessState
            displayJoinIcon
            flightroom={room}
            key={`searchable-${room.id}`}
            onClick={() => onClickSearchableFlightroom(room)}
          />
        )),
      )}
      {isFetchingNextPage ? (
        <span className="flightrooms-content-fetching-spinner">
          <Spinner size="normal" />
        </span>
      ) : null}
    </section>
  );
};
